/**
 * Field Registry for the flat products API (Config C — ProductsRowGroupingDataGrid)
 *
 * Separate from PRODUCT_FIELDS_REGISTRY which uses the hierarchical Product type.
 * This registry uses the denormalized FlatProductRow type from the flat API.
 */

import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { FlatProductRow } from '../../fixtures/flatProductData';

export const FLAT_PRODUCT_FIELDS: BaseFieldDefinition<FlatProductRow>[] = [
  // Product fields
  {
    key: 'product_cas_name',
    label: 'CAS Registry Name',
    dataType: 'string',
    description: 'Product name from CAS Registry API',
    isRequired: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'preferred_product_name',
    label: 'Product Name',
    dataType: 'string',
    description:
      'Computed display name: user-set name, Orange Book ingredient, or CAS name (in priority order)',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'cas_number',
    label: 'CAS Number',
    dataType: 'string',
    description: 'Chemical Abstracts Service registry number',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'is_cas_verified',
    label: 'CAS Verified',
    dataType: 'boolean',
    description: 'Whether the CAS number has been verified',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'is_fda_linked',
    label: 'Orange Book Linked',
    dataType: 'boolean',
    description: 'Indicates whether the product is linked to Orange Book data',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'is_ndc_linked',
    label: 'NDC Linked',
    dataType: 'boolean',
    description:
      'Indicates whether the product is linked to FDA NDC Directory data',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'molecular_formula',
    label: 'Molecular Formula',
    dataType: 'string',
    description: 'Chemical molecular formula',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'molecular_mass',
    label: 'Molecular Mass',
    dataType: 'string',
    description: 'Molecular mass of the compound',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'note',
    label: 'Note',
    dataType: 'string',
    description: 'Additional notes about the product',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'uri',
    label: 'URI',
    dataType: 'string',
    description: 'Uniform Resource Identifier for the product',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'inchi',
    label: 'InChI',
    dataType: 'string',
    description: 'International Chemical Identifier',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'inchi_key',
    label: 'InChI Key',
    dataType: 'string',
    description: 'Hashed version of InChI',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'smile',
    label: 'SMILES',
    dataType: 'string',
    description: 'Simplified Molecular Input Line Entry System',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'canonical_smile',
    label: 'Canonical SMILES',
    dataType: 'string',
    description: 'Canonical SMILES notation',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'created_date',
    label: 'Created Date',
    dataType: 'date',
    description: 'Date when the product record was created',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    dataType: 'date',
    description: 'Date when the product record was last modified',
    isSortable: true,
    isFilterable: true,
  },

  // Manufacturer fields
  {
    key: 'manufacturer_name',
    label: 'Manufacturer',
    dataType: 'string',
    description: 'Manufacturer name',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'manufacturing_status',
    label: 'Manufacturing Status',
    dataType: 'string',
    description: 'Commercial or R&D status',
    isSortable: true,
    isFilterable: true,
  },

  // Facility fields
  {
    key: 'facility_name',
    label: 'Facility',
    dataType: 'string',
    description: 'Manufacturing facility name',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
  {
    key: 'country_code',
    label: 'Country',
    dataType: 'string',
    description: 'ISO country code',
    isSortable: true,
    isFilterable: true,
  },
  {
    key: 'facility_status',
    label: 'Facility Status',
    dataType: 'string',
    description: 'Facility operational status',
    isSortable: true,
    isFilterable: true,
  },

  // Grade fields
  {
    key: 'grade_name',
    label: 'Product Type',
    dataType: 'string',
    description: 'Product grade or type (e.g., API, USP)',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },

  // Therapeutic Area fields
  {
    key: 'therapeutic_area_name',
    label: 'Therapeutic Area',
    dataType: 'string',
    description: 'Therapeutic area classification',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },

  // Orange Book fields
  {
    key: 'ob_product_name',
    label: 'Orange Book Product Name',
    dataType: 'string',
    description: 'Ingredient name from the FDA Orange Book (most recent entry)',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
  },
];
